import { useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router';
import { useTranslation } from 'react-i18next';
import { LessonNode, Stack, UnitBanner, type UnitBannerTone } from '@fluent/ui';
import type {
  ProgressionView,
  TreeLessonNode,
  TreeSkillGroup,
  TreeUnitGroup,
} from '../progress/progression.js';
import { useInProgressSessions } from '../lesson/sessionStore.js';
import { useFreeAccess, useProAccessState } from '../subscription/hooks.js';
import { isLessonGated } from '../subscription/access.js';
import { playSfx } from '../lesson/sounds.js';
import './SkillTree.css';

/** How the next lesson's coin moves (Tornike 2026-09-29: no "დაიწყე" bubble —
 *  "just make some moving animation on a current lesson"; he picked wiggle). */
export type NextMotion = 'hop' | 'pulse' | 'wiggle';

export interface SkillTreeProps {
  view: ProgressionView;
  /** Which motion marks the next lesson. The /skill-tree-review route passes
   *  `?motion=` so the options can be compared; Home uses the default. */
  nextMotion?: NextMotion;
}

/**
 * SkillTree — the vertical home tree. Each unit gets a UnitBanner; under
 * each unit, the 4 skills render as their own block with a small
 * skill-header card + a 4-lesson serpentine. This split was added
 * 2026-04-29 — Tornike flagged that running 16 lessons of unit 1 as one
 * long sine-wave hides the skill structure from the user.
 *
 * Sine-wave math borrowed from Pilot C (DECISIONS 2026-04-21). Connector
 * line stays in the DOM at opacity 0 per Tornike's final call.
 */
/** Total number of chameleon characters in the rotating set
 *  (apps/app/public/characters/character-01.png … character-18.png).
 *  ONE character per SKILL (უნარი), picked by global skill index across
 *  the whole course. A1 has 30 skills → the cycle wraps. When A2 ships,
 *  the wrap continues seamlessly.
 *
 *  CURATED ROSTER (2026-07-17): 6 of the original 18 sheets carry
 *  AI-gibberish English baked into the art (clipboard "Deit/Bock",
 *  "Let's leam", garbled chalkboard/speech bubbles/book spines) — fatal
 *  on an app that teaches English. Those never render. 01/12/16/18 were
 *  paint-patched clean (scripts/patch-stickers.py); the rest are
 *  text-free originals. */
const CHARACTER_ROSTER = [1, 4, 8, 9, 10, 12, 13, 14, 15, 16, 17, 18] as const;
const CHARACTER_COUNT = CHARACTER_ROSTER.length;

/** Hand-placed lean per sticker, cycled by roster slot — deterministic
 *  "someone stuck these in an album" rhythm (never two alike nearby).
 *  Applied to the sticker image only; the star meter stays level. */
const STICKER_TILTS = ['-3deg', '2.5deg', '-2deg', '3.5deg', '-1.5deg', '2deg'] as const;

export function SkillTree({ view, nextMotion = 'wiggle' }: SkillTreeProps) {
  // Walk units to build a cumulative skill counter so each SkillBlock
  // gets a globally-unique character index. Repetition is unavoidable
  // past skill 18 but the cycle is wide enough that no two adjacent
  // skills share a character.
  let skillsBefore = 0;
  // Cumulative LESSON counter — drives the entrance-stagger delay
  // (--node-i) so the whole tree cascades top-to-bottom on Home mount.
  // The CSS clamps the delay at 500ms, so deep nodes pop together.
  let lessonsBefore = 0;

  return (
    <Stack gap={8} data-testid="skill-tree" data-next-motion={nextMotion}>
      {view.units.map((unitGroup, i) => {
        const startSkillIndex = skillsBefore;
        const startNodeIndex = lessonsBefore;
        skillsBefore += unitGroup.skills.length;
        lessonsBefore += unitGroup.skills.reduce((sum, s) => sum + s.lessons.length, 0);
        return (
          <UnitBlock
            key={unitGroup.unit.id}
            unitGroup={unitGroup}
            unitNumber={i + 1}
            nextLessonId={view.nextLessonId}
            tone={UNIT_TONES[i % UNIT_TONES.length]}
            tint={UNIT_TINTS[i % UNIT_TINTS.length]}
            startSkillIndex={startSkillIndex}
            startNodeIndex={startNodeIndex}
          />
        );
      })}
    </Stack>
  );
}

const UNIT_TONES: UnitBannerTone[] = ['primary', 'accent', 'xp', 'info'];

/**
 * Background tint per unit — paired by index with `UNIT_TONES` so a
 * green-banner unit sits on a mint zone, a terracotta-banner unit on
 * peach, etc. Defined as a typed string union (not enum) so it lives
 * cleanly as a `data-tint` attribute consumed by CSS.
 */
type UnitTint = 'mint' | 'peach' | 'sand' | 'sky';
const UNIT_TINTS: UnitTint[] = ['mint', 'peach', 'sand', 'sky'];

interface UnitBlockProps {
  unitGroup: TreeUnitGroup;
  unitNumber: number;
  nextLessonId: string | null;
  tone: UnitBannerTone;
  tint: UnitTint;
  /** Global skill index of this unit's first skill — drives per-skill
   *  mascot selection so every skill in the course gets a unique-ish
   *  chameleon (cycles every 18). */
  startSkillIndex: number;
  /** Global lesson index of this unit's first lesson — drives the
   *  entrance-stagger delay (--node-i) on each node slot. */
  startNodeIndex: number;
}

function UnitBlock({
  unitGroup,
  unitNumber,
  nextLessonId,
  tone,
  tint,
  startSkillIndex,
  startNodeIndex,
}: UnitBlockProps) {
  const { t } = useTranslation();
  // Walk this unit's skills to hand each serpentine its global lesson
  // start index (same cumulative-counter pattern as SkillTree above).
  let nodesBefore = startNodeIndex;

  return (
    <div className="fluent-home__unit" data-tint={tint}>
      <UnitBanner
        tone={tone}
        kicker={t('home.unit_kicker', { n: unitNumber })}
        title={unitGroup.unit.title}
        description={unitGroup.unit.description}
        icon={UNIT_EMOJI[unitNumber - 1] ?? '✨'}
      />
      <Stack gap={6} className="fluent-home__skills">
        {unitGroup.skills.map((skillGroup, i) => {
          const skillNodeStart = nodesBefore;
          nodesBefore += skillGroup.lessons.length;
          return (
            <SkillBlock
              key={skillGroup.skill.id}
              skillGroup={skillGroup}
              skillNumber={i + 1}
              totalSkills={unitGroup.skills.length}
              // Alternating direction: skill 1 bends LEFT, skill 2 RIGHT,
              // skill 3 LEFT, skill 4 RIGHT. Stacks the unit into one
              // big snake through the whole unit.
              bend={i % 2 === 0 ? 'left' : 'right'}
              nextLessonId={nextLessonId}
              characterIndex={(startSkillIndex + i) % CHARACTER_COUNT}
              startNodeIndex={skillNodeStart}
            />
          );
        })}
      </Stack>
    </div>
  );
}

interface SkillBlockProps {
  skillGroup: TreeSkillGroup;
  skillNumber: number;
  totalSkills: number;
  bend: 'left' | 'right';
  nextLessonId: string | null;
  /** Roster slot (0..CHARACTER_COUNT-1) of the chameleon hosting this skill. */
  characterIndex: number;
  /** Global lesson index of this skill's first lesson (entrance stagger). */
  startNodeIndex: number;
}

function SkillBlock({
  skillGroup,
  skillNumber,
  totalSkills,
  bend,
  nextLessonId,
  characterIndex,
  startNodeIndex,
}: SkillBlockProps) {
  const { t } = useTranslation();
  const allComplete = skillGroup.lessons.every(
    (l) => l.state === 'complete' || l.state === 'golden',
  );
  const characterNum = String(CHARACTER_ROSTER[characterIndex]).padStart(2, '0');

  // Star-lit sound: detect when this skill's completed-lesson count
  // GROWS, then play one sparkle per newly-lit star (staggered to match
  // the CSS pop-in cascade). Skip on first mount — only fire when the
  // count actually increases (e.g. user finishes a lesson and returns).
  const litCount = skillGroup.lessons.filter(
    (l) => l.state === 'complete' || l.state === 'golden',
  ).length;
  const prevLitCountRef = useRef(litCount);
  useEffect(() => {
    const prev = prevLitCountRef.current;
    prevLitCountRef.current = litCount;
    if (litCount <= prev) return;
    const newStars = litCount - prev;
    for (let i = 0; i < newStars; i++) {
      window.setTimeout(() => playSfx('star_lit'), i * 90);
    }
  }, [litCount]);

  return (
    <section
      className="fluent-home__skill"
      data-complete={allComplete || undefined}
      aria-labelledby={`skill-${skillGroup.skill.id}-title`}
    >
      {/* One chameleon per უნარი (skill). Sits beside the serpentine in
       *  the empty horizontal space, on the side OPPOSITE the path's bend
       *  so it never overlaps a lesson node. Decorative only.
       *
       *  Below the chameleon: 4 stars, one per lesson in the skill. Each
       *  lit star marks a completed lesson. Stars use a one-shot scale +
       *  glow animation on mount so when the user returns after finishing
       *  a lesson, the newly-lit star pops in. A `star_lit` sparkle SFX
       *  + tiny haptic fires from useEffect when the lit count actually
       *  increases (skips on initial mount to avoid noise on every visit). */}
      <div className="fluent-home__skill-mascot" data-bend={bend}>
        <img
          className="fluent-home__skill-mascot__img"
          src={`/characters/sticker-${characterNum}.png`}
          style={{ ['--tilt' as string]: STICKER_TILTS[characterIndex % STICKER_TILTS.length] }}
          alt=""
          aria-hidden="true"
          loading="lazy"
          decoding="async"
        />
        <div
          className="fluent-home__skill-stars"
          role="meter"
          aria-valuemin={0}
          aria-valuemax={skillGroup.lessons.length}
          aria-valuenow={litCount}
          aria-label={`${litCount} / ${skillGroup.lessons.length}`}
        >
          {skillGroup.lessons.map((lesson, i) => {
            const lit = lesson.state === 'complete' || lesson.state === 'golden';
            return (
              <span
                key={lesson.lessonId}
                className="fluent-home__skill-star"
                data-lit={lit || undefined}
                data-pos={i}
                style={{ ['--i' as string]: i }}
                aria-hidden="true"
              >
                <svg
                  viewBox="0 0 24 24"
                  className="fluent-home__skill-star-svg"
                  aria-hidden="true"
                >
                  {/* 5-point star body. Stroke gives it the chunky
                   *  comic outline; shine path is a small upper-left
                   *  triangle that fades in only when lit. */}
                  <path
                    className="fluent-home__skill-star-body"
                    d="M12 1.8 L15 8.8 L22.6 9.5 L16.7 14.7 L18.6 22.2 L12 18.2 L5.4 22.2 L7.3 14.7 L1.4 9.5 L9 8.8 Z"
                    strokeLinejoin="round"
                    strokeLinecap="round"
                  />
                  <path
                    className="fluent-home__skill-star-shine"
                    d="M9 5.5 L11.2 7 L9.6 9.4 Z"
                  />
                </svg>
              </span>
            );
          })}
        </div>
      </div>
      <header className="fluent-home__skill-header">
        <p className="fluent-home__skill-kicker">
          {t('home.skill_kicker', { n: skillNumber, total: totalSkills })}
        </p>
        <h3 id={`skill-${skillGroup.skill.id}-title`} className="fluent-home__skill-title">
          {skillGroup.skill.title}
          {allComplete && (
            <span className="fluent-home__skill-tick" aria-hidden="true">
              ✓
            </span>
          )}
        </h3>
        {skillGroup.skill.description && (
          <p className="fluent-home__skill-desc">{skillGroup.skill.description}</p>
        )}
      </header>
      <Serpentine
        lessons={skillGroup.lessons}
        nextLessonId={nextLessonId}
        bend={bend}
        startNodeIndex={startNodeIndex}
      />
    </section>
  );
}

const UNIT_EMOJI = ['👋', '👥', '🏠', '🚶', '⏰', '📅'];

/** Emoji stand-ins for unit illustrations until Phase 11 commissions real art. */

interface SerpentineProps {
  lessons: TreeLessonNode[];
  nextLessonId: string | null;
  /** Which way the skill's path bends: left = lessons offset to the
   *  left of center, right = lessons offset to the right. Alternates
   *  per skill so consecutive skills stack into one big snake. */
  bend: 'left' | 'right';
  /** Global lesson index of this serpentine's first node — feeds the
   *  --node-i CSS var so the entrance stagger cascades across the whole
   *  tree, not per-skill. */
  startNodeIndex: number;
}

function Serpentine({ lessons, nextLessonId, bend, startNodeIndex }: SerpentineProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(360);
  const nextNodeRef = useRef<HTMLDivElement>(null);
  const sessions = useInProgressSessions();
  const access = useFreeAccess();
  const proAccess = useProAccessState();

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width;
      if (w) setWidth(w);
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Scroll the "next" lesson into view on mount so returning users land
  // right where they left off — but ONLY if it is not already on screen.
  // It used to scroll unconditionally, which threw the daily-missions card
  // off the top of a new learner's Home every single time (audit F044).
  useEffect(() => {
    const el = nextNodeRef.current;
    if (!el) return;
    const r = el.getBoundingClientRect();
    // Off screen entirely — not merely "not centred". A new learner's first
    // node sits just below the fold; centring it there would push the daily
    // missions card off the top before they have seen it once. 64px clears
    // the top bar, 96px the bottom tab bar.
    const offScreen = r.bottom < 64 || r.top > window.innerHeight - 96;
    if (offScreen) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
  }, []);

  const layout = useMemo(() => {
    // 2026-04-29 — final spec from Tornike: each SKILL bends in one
    // direction (alternating across skills). Within a skill, lessons
    // form a half-arc on that side — first lesson at center, lessons
    // 2-3 at peak, last back toward center. Stacking left-bending
    // skill + right-bending skill = one continuous S through the unit.
    //
    // Amplitude clamped 70–110 so peaks stay inside the readable
    // column on every phone size.
    const amplitude = Math.min(Math.max((width - 180) / 3, 70), 110);
    const sign = bend === 'left' ? -1 : 1;
    const verticalStep = 144;
    const nodeHeight = 72;
    const n = lessons.length;
    const totalSpan = Math.max(0, (n - 1) * verticalStep);
    // Half-sine — t goes 0 → 1, sin(t * π) gives a single hump 0 → 1 → 0.
    // Multiplied by sign × amplitude: lessons at the start + end sit at
    // center; middle lessons sit at peak on the bend side.
    const xs = lessons.map((_, i) => {
      const t = n <= 1 ? 0 : i / (n - 1);
      return sign * Math.sin(t * Math.PI) * amplitude;
    });
    const ys = lessons.map((_, i) => i * verticalStep);
    return {
      xs,
      ys,
      totalHeight: totalSpan + nodeHeight + 40,
    };
  }, [bend, lessons, width]);

  return (
    <div
      ref={containerRef}
      className="fluent-home__serpentine"
      style={{ height: layout.totalHeight }}
    >
      {lessons.map((lesson, i) => {
        const isNext = lesson.lessonId === nextLessonId;
        // Partial-progress drives the ring fill on in-progress nodes. If the
        // user has a saved session for this lesson, show how far they got.
        // Completed lessons always show full ring; everything else (no
        // session, no completion) shows zero.
        const session = sessions[lesson.lessonId];
        const completedCount =
          lesson.state === 'complete' || lesson.state === 'golden'
            ? lesson.exerciseCount
            : session
              ? // A saved session is never a finished lesson: since retries
                // (2026-10-08) it can hold every exercise and still owe the
                // second tries of its mistakes — so never a full ring.
                Math.min(session.results.length, lesson.exerciseCount - 1)
              : 0;
        // Pro-locked: visually distinct from progression-locked. We still
        // let the user TAP the node so they hit the paywall (instead of
        // dead-ending). The paywall navigates back here on cancel.
        // Only 'no' locks: while the subscription read is in flight the
        // answer is unknown, and padlocking a subscriber's whole tree for
        // the first frames of every cold load is the same bug the lesson
        // player had. Unknown → no decoration; the lock appears (or never
        // does) a round-trip later.
        const proLocked =
          proAccess === 'no' && isLessonGated(access, lesson.lessonId);
        const nodeBody = (
          <LessonNode
            state={lesson.state}
            label={lesson.title}
            total={lesson.exerciseCount}
            completed={completedCount}
            breathe={isNext && !proLocked}
            disabled={lesson.state === 'locked'}
            aria-label={lesson.title}
            data-testid={`node-${lesson.lessonId}`}
            data-pro-locked={proLocked || undefined}
          />
        );

        // The serpentine X offset lives in a CSS var (--node-x) instead of an
        // inline transform: the entrance-stagger keyframes animate transform
        // with fill-mode both, and an inline transform would be overridden
        // during the cascade (nodes would lose their curve offset, then
        // snap). The base rule + keyframes both compose
        // translateX(calc(-50% + var(--node-x))). --node-i is the GLOBAL
        // lesson index driving the stagger delay (clamped at 500ms in CSS).
        // The stagger runs once per Home mount only: slots are keyed by
        // stable lesson ids (key={lesson.lessonId} below), so progress
        // refetches re-render without remounting and the CSS animation
        // does not replay.
        const positioned = (
          <div
            ref={isNext ? nextNodeRef : undefined}
            className="fluent-home__node-slot"
            data-next={(isNext && !proLocked) || undefined}
            style={{
              top: `${layout.ys[i]}px`,
              ['--node-x' as string]: `${layout.xs[i]}px`,
              ['--node-i' as string]: startNodeIndex + i,
            }}
          >
            {lesson.state === 'locked' ? (
              nodeBody
            ) : (
              <Link
                to={`/learn/${lesson.lessonId}`}
                className="fluent-home__node-link"
                aria-label={lesson.title}
              >
                {nodeBody}
              </Link>
            )}
          </div>
        );

        return <div key={lesson.lessonId}>{positioned}</div>;
      })}
    </div>
  );
}
